import {
  getSocket,
  addSocketOpenListener,
  addSocketReplacedListener,
  addSessionEndedListener,
} from './channelService';

/**
 * Multi-channel manager for team-boards realtime. Unlike workspaceChannelService
 * (one active workspace channel), a user can belong to several teams at once, so
 * this joins one `team:{teamId}` phoenix channel per team and fans every inbound
 * application frame out to listeners, tagged with the originating teamId.
 *
 * Mirrors the workspace channel semantics: a second join 'ok' after a socket
 * reconnect means broadcasts may have been missed → emit TEAM_REJOINED_EVENT so
 * consumers can gap-fill (a team pull).
 */
export const TEAM_REJOINED_EVENT = '__team_rejoined';

// Ephemeral, unpersisted block-level collab ops (see attachChannelListeners —
// this event needs no special wiring: onMessage below already fans out ANY
// inbound event by name through addTeamEventListener, exactly like sync_delta/
// presence_state/presence_diff do). Exported so consumers reference the event
// name instead of the literal string.
export const COLLAB_BLOCK_OPS_EVENT = 'collab:block_ops';

// teamId -> { channel }
const channels = new Map();
// desired membership so a socket that connects later can (re)join.
const desired = new Set();
// teamId -> merged presence context ({ board_id?, document_id?, task_id?,
// block_id? }, keys accumulate across calls — see pushTeamPresenceContext).
// Presence.track re-runs from scratch on every join, so the full context set
// before the channel finished joining — or before a reconnect's fresh join —
// must be replayed in full, not just the latest partial push.
const desiredContext = new Map();
let socketOpenUnsub = null;
let socketReplacedUnsub = null;
// event name -> Set<fn>. Handlers receive (payload, { teamId }).
const eventHandlers = new Map();

function ensureHandlerSet(event) {
  if (!eventHandlers.has(event)) eventHandlers.set(event, new Set());
  return eventHandlers.get(event);
}

function dispatchEvent(event, payload, teamId) {
  ensureHandlerSet(event).forEach((fn) => {
    try {
      fn(payload, { teamId });
    } catch (err) {
      console.error(`Team channel handler error (${event}):`, err);
    }
  });
}

function attachChannelListeners(channel, teamId) {
  channel.onMessage = (event, payload) => {
    if (typeof event === 'string' && !event.startsWith('phx_') && !event.startsWith('chan_reply_')) {
      dispatchEvent(event, payload, teamId);
    }
    return payload;
  };
}

function doJoin(teamId) {
  const socket = getSocket();
  if (!socket) return; // retried on socket open (armed by ensureSocketRetry)
  if (channels.has(teamId)) return;

  const channel = socket.channel(`team:${teamId}`, {});
  channels.set(teamId, { channel });
  attachChannelListeners(channel, teamId);

  let joinOkCount = 0;
  let hadError = false;
  // Any errored stretch (unauthorized join while the token was stale, socket
  // drop, timeout) may have swallowed broadcasts — the NEXT successful join
  // must gap-fill even if it is this channel object's first 'ok', which the
  // joinOkCount > 1 test alone would miss.
  channel.onError(() => {
    hadError = true;
  });
  channel
    .join()
    .receive('ok', () => {
      joinOkCount += 1;
      if (joinOkCount > 1 || hadError) {
        dispatchEvent(TEAM_REJOINED_EVENT, { team_id: teamId }, teamId);
      }
      hadError = false;
      // Presence.track re-tracks from scratch on every join, so any context
      // set before this join finished (or before a reconnect) is gone
      // server-side — replay the full accumulated context now rather than
      // leaving the roster silently missing this client until its next
      // board/document/task switch.
      const ctx = desiredContext.get(teamId);
      if (ctx && Object.keys(ctx).length) channel.push('presence:context', ctx);
    })
    .receive('error', (err) => {
      hadError = true;
      console.warn(`Team channel join error (${teamId}):`, err);
    })
    .receive('timeout', () => {
      hadError = true;
      console.warn(`Team channel join timed out (${teamId})`);
    });
}

function ensureSocketRetry() {
  if (socketOpenUnsub) return;
  socketOpenUnsub = addSocketOpenListener(() => {
    // Rejoin every desired team once the socket (re)connects.
    desired.forEach((teamId) => doJoin(teamId));
  });
}

// COMMS-04: a region failover replaces channelService's socket outright, but
// every Channel object in `channels` still points at the old, now-dead one.
// doJoin's `channels.has(teamId)` guard would otherwise treat that stale
// entry as "already joined" forever and silently no-op every future rejoin.
// On this signal, drop the stale entries (no leave() — there is nothing
// listening on the dead transport) and rejoin each desired team for real on
// the new socket.
function ensureSocketReplacedRetry() {
  if (socketReplacedUnsub) return;
  socketReplacedUnsub = addSocketReplacedListener(() => {
    channels.clear();
    desired.forEach((teamId) => doJoin(teamId));
  });
}

// A session end clears channelService's socketOpenHandlers/
// socketReplacedHandlers Sets, but ensureSocketRetry()/
// ensureSocketReplacedRetry() above only ever register into them ONCE per
// module lifetime (guarded by socketOpenUnsub/socketReplacedUnsub). Without
// resetting those guards too, they stay non-null forever after the first
// session end, so no later login this app session ever re-subscribes —
// silently reintroducing COMMS-04 (a later failover/reconnect swaps in a
// socket nobody told this service about) starting from the second login.
//
// Guard-only was not enough on its own, though (see addSessionEndedListener's
// invariant doc): `channels`/`desired`/`desiredContext` are this module's
// PRIMARY state — which teams are joined for THIS account — and until this
// fix they were reset only by leaveAllTeamChannels() being called from
// useTeamBoardsRealtime.js's own effect cleanup (keyed on isAuthenticated), a
// mounted-component dependency, not a guarantee. A session end not
// immediately followed by that effect re-running left the departed
// account's joined teams in `desired`, so the NEXT account's first socket
// failover silently rejoined them under the new session (a cross-account
// leak, not just a stale retry guard). leaveAllTeamChannels() resets
// everything this module mirrors, so calling it here — in addition to
// nulling the guards — is what makes this module actually reset ITSELF.
//
// Registered once, unconditionally, at module load — this subscription
// itself must survive every teardown, so it does not go through either
// guarded ensure* function.
addSessionEndedListener(() => {
  socketOpenUnsub = null;
  socketReplacedUnsub = null;
  leaveAllTeamChannels();
});

export function joinTeamChannel(teamId) {
  if (teamId == null) return;
  const id = String(teamId);
  desired.add(id);
  ensureSocketRetry();
  ensureSocketReplacedRetry();
  doJoin(id);
}

export function leaveTeamChannel(teamId) {
  const id = String(teamId);
  desired.delete(id);
  desiredContext.delete(id);
  const entry = channels.get(id);
  if (entry?.channel) entry.channel.leave();
  channels.delete(id);
}

export function leaveAllTeamChannels() {
  channels.forEach((entry) => entry.channel?.leave());
  channels.clear();
  desired.clear();
  desiredContext.clear();
}

export function addTeamEventListener(event, fn) {
  ensureHandlerSet(event).add(fn);
  return () => eventHandlers.get(event)?.delete(fn);
}

/**
 * "I'm viewing board/document/task/block X" — piggybacks on the already-
 * tracked team presence entry via Presence.update on the backend, so other
 * clients can filter the team roster down to a specific board/document/task,
 * or down to block-level presence within one.
 *
 * Two call shapes:
 * - `pushTeamPresenceContext(teamId, { board_id?, document_id?, task_id?,
 *   block_id? })` — the current shape. Only the keys present in `context` are
 *   touched: an explicit `null` clears that key server-side, an absent key
 *   leaves whatever was set by an earlier call untouched (e.g. opening a task
 *   detail modal over an open board sets task_id without losing board_id).
 * - `pushTeamPresenceContext(teamId, boardIdOrNull)` — legacy shape, kept for
 *   existing callers (TaskBoardTab): a bare id/null is converted to
 *   `{ board_id: boardIdOrNull }`.
 */
export function pushTeamPresenceContext(teamId, contextOrBoardId) {
  if (teamId == null) return;
  const id = String(teamId);
  const isLegacyShape = contextOrBoardId === null || typeof contextOrBoardId !== 'object';
  const partial = isLegacyShape ? { board_id: contextOrBoardId ?? null } : contextOrBoardId || {};
  desiredContext.set(id, { ...(desiredContext.get(id) || {}), ...partial });
  const entry = channels.get(id);
  entry?.channel?.push('presence:context', partial);
}

/**
 * Ephemeral, unpersisted block-level collab op(s) for one document/task —
 * fanned out server-side via broadcast_from! (sender excluded) to the rest of
 * the team channel; the debounced LWW record save remains the only path that
 * persists anything. `payload` is `{ entity_type: 'document'|'task',
 * entity_id, ops }`. No-op (silently) when the team channel isn't joined.
 */
export function pushCollabOps(teamId, payload) {
  if (teamId == null) return;
  const id = String(teamId);
  const entry = channels.get(id);
  entry?.channel?.push(COLLAB_BLOCK_OPS_EVENT, payload);
}
